<template>
  <div>
    <ShowPageHeader :backLabel="t('back')" backRoute="/internal/users" :title="pageTitle" />
    <Loading v-if="loading" />
    <div v-else-if="insurerManager" class="mt-6 space-y-6">
      <div class="bg-white shadow-sm sm:rounded-md p-6 space-y-2">
        <FieldRow :label="t('email')" :value="insurerManager.email" />
        <FieldRow :label="t('role')" :value="t(`role_${insurerManager.role}`)" />
      </div>

      <div v-if="isSalesManager" class="bg-white shadow-sm sm:rounded-md p-6">
        <h2 class="text-lg font-semibold text-gray-900">{{ t("stores_title") }}</h2>
        <p class="text-sm text-gray-500 mt-1">{{ t("stores_subtitle") }}</p>

        <CardSelector
          v-model="selectedStoreIds"
          :items="stores"
          label-key="salesChannel"
          value-key="id"
          :placeholder="t('stores_search_placeholder')"
          class="mt-4"
        />

        <div class="flex justify-end mt-6">
          <PrimaryButton :loading="saving" @click="save">{{ t("save") }}</PrimaryButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { ShowPageHeader, Loading, FieldRow, PrimaryButton, CardSelector } from "@estaly/ui";

const ALL_STORES_PER_PAGE = 1000;

export default {
  components: { ShowPageHeader, Loading, FieldRow, PrimaryButton, CardSelector },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      loading: true,
      saving: false,
      selectedStoreIds: [],
    };
  },

  computed: {
    ...mapState("insurerManagers", { insurerManager: "current" }),
    ...mapState("stores", { stores: "index" }),

    isSalesManager() {
      return this.insurerManager?.role === "sales_manager";
    },

    pageTitle() {
      if (!this.insurerManager) return "";
      return [this.insurerManager.firstName, this.insurerManager.lastName].filter(Boolean).join(" ");
    },
  },

  async created() {
    await this.retrieve();
  },

  methods: {
    ...mapActions("insurerManagers", { fetchInsurerManager: "fetchShow", updateInsurerManager: "update" }),
    ...mapActions("stores", { fetchStores: "fetchIndex" }),
    ...mapActions("notifications", ["notify"]),

    async retrieve() {
      this.loading = true;
      try {
        await this.fetchInsurerManager(this.$route.params.id);
        this.selectedStoreIds = (this.insurerManager?.stores || []).map((store) => store.id);
        if (this.isSalesManager) {
          await this.fetchStores({ perPage: ALL_STORES_PER_PAGE, order: "sales_channel" });
        }
      } finally {
        this.loading = false;
      }
    },

    async save() {
      this.saving = true;
      try {
        const response = await this.updateInsurerManager({
          id: this.insurerManager.id,
          storeIds: this.selectedStoreIds,
        });
        if (!response?.success) return;

        this.notify({
          type: "success",
          category: "simple",
          title: this.t("notify_title"),
          text: this.t("notify_text"),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<i18n src="./InsurerManagerShow.i18n.json"></i18n>
